import React, { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, DollarSign, Users, UserCheck, TrendingDown, ArrowUpRight, RefreshCw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useRevenueMetrics, useAdminSubscriptions, useRevenueHistory } from '../../hooks/useAdminRevenue';

const PER_PAGE = 25;

const STATUS_TABS = [
  { label: 'Todos', value: null },
  { label: 'Ativos', value: 'active' },
  { label: 'Trial', value: 'trialing' },
  { label: 'Cancelados', value: 'canceled' },
  { label: 'Vencidos', value: 'past_due' },
];

const statusBadge = (status: string) => {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    active: { bg: 'bg-green-500/10', text: 'text-green-400', label: 'Ativo' },
    trialing: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Trial' },
    canceled: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Cancelado' },
    past_due: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', label: 'Vencido' },
    unpaid: { bg: 'bg-orange-500/10', text: 'text-orange-400', label: 'Não pago' },
    paused: { bg: 'bg-neutral-500/10', text: 'text-neutral-400', label: 'Pausado' },
  };
  const s = map[status] || { bg: 'bg-neutral-500/10', text: 'text-neutral-400', label: status };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>{s.label}</span>;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString('pt-BR');

const AvatarWithFallback: React.FC<{ src: string | null; name: string | null }> = ({ src, name }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    const initials = (name || '?').slice(0, 2).toUpperCase();
    return (
      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-medium text-neutral-400">
        {initials}
      </div>
    );
  }
  return <img src={src} alt="" className="w-8 h-8 rounded-full" onError={() => setFailed(true)} />;
};

export const AdminRevenue: React.FC = () => {
  const { metrics, loading: metricsLoading, error: metricsError, refetch } = useRevenueMetrics();
  const { history, loading: historyLoading } = useRevenueHistory(12);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter]);

  const { subscriptions, total, loading: subsLoading, error: subsError } = useAdminSubscriptions({
    status: statusFilter,
    search: debouncedSearch,
    page,
  });

  const totalPages = Math.ceil(total / PER_PAGE);

  const chartData = history.map(m => ({
    month: m.month.slice(5),
    mrr: m.active_count * 29.90,
  }));

  const kpiCards = metrics
    ? [
        { label: 'MRR', value: formatCurrency(metrics.mrr), icon: DollarSign, color: 'text-green-400' },
        { label: 'Assinaturas Ativas', value: String(metrics.active_subscriptions), icon: UserCheck, color: 'text-blue-400' },
        { label: 'Usuários em Trial', value: String(metrics.trialing_users), icon: Users, color: 'text-purple-400' },
        { label: 'Churn Rate', value: `${metrics.churn_rate}%`, icon: TrendingDown, color: 'text-red-400' },
        { label: 'Trial → Pago', value: `${metrics.trial_conversion_rate}%`, icon: ArrowUpRight, color: 'text-emerald-400' },
      ]
    : [];

  if (metricsError) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-white mb-6">Receita & Assinaturas</h1>
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
          <p className="text-red-400 mb-3">Erro ao carregar métricas: {metricsError}</p>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Receita & Assinaturas</h1>

      {/* KPI Cards */}
      {metricsLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-neutral-900/50 border border-white/10 rounded-xl p-5 animate-pulse h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {kpiCards.map(card => (
            <div key={card.label} className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <card.icon className={`w-4 h-4 ${card.color}`} />
                <span className="text-xs text-neutral-500 uppercase tracking-wide">{card.label}</span>
              </div>
              <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* MRR Chart */}
      <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-5 mb-8">
        <h2 className="text-sm font-medium text-neutral-400 mb-4">Evolução MRR (12 meses)</h2>
        {historyLoading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-neutral-500 text-sm">
            Nenhum dado de receita disponível
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData}>
              <XAxis dataKey="month" stroke="#525252" tick={{ fill: '#a3a3a3', fontSize: 12 }} />
              <YAxis
                stroke="#525252"
                tick={{ fill: '#a3a3a3', fontSize: 12 }}
                tickFormatter={(v: number) => `R$${(v / 1000).toFixed(1)}k`}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#171717', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                labelStyle={{ color: '#a3a3a3' }}
                formatter={(value: number) => [formatCurrency(value), 'MRR']}
              />
              <Line type="monotone" dataKey="mrr" stroke="#4ade80" strokeWidth={2} dot={{ r: 3, fill: '#4ade80' }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Subscriptions Section */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-neutral-900 border border-white/10 rounded-lg text-sm text-white placeholder-neutral-500"
          />
        </div>
        <div className="flex gap-1 overflow-x-auto whitespace-nowrap">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.label}
              onClick={() => setStatusFilter(tab.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === tab.value
                  ? 'bg-white/10 text-white'
                  : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Subscriptions Error */}
      {subsError && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          Erro ao carregar assinaturas: {subsError}
        </div>
      )}

      {/* Subscriptions Table */}
      <div tabIndex={0} className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-white/10 text-neutral-400 text-left">
              <th className="p-4">Usuário</th>
              <th className="p-4">Email</th>
              <th className="p-4">Status</th>
              <th className="p-4">Plano</th>
              <th className="p-4">Início</th>
              <th className="p-4">Próxima Cobrança</th>
            </tr>
          </thead>
          <tbody>
            {subsLoading ? (
              <tr>
                <td colSpan={6} className="p-8 text-center">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : subscriptions.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-neutral-500">
                  Nenhuma assinatura encontrada
                </td>
              </tr>
            ) : (
              subscriptions.map(sub => (
                <tr key={sub.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="p-4 flex items-center gap-3">
                    <AvatarWithFallback src={sub.user_avatar} name={sub.user_name} />
                    <span className="text-white">{sub.user_name || '—'}</span>
                  </td>
                  <td className="p-4 text-neutral-400 max-w-[180px] truncate lg:max-w-none">{sub.user_email}</td>
                  <td className="p-4">{statusBadge(sub.status)}</td>
                  <td className="p-4 text-neutral-400">{formatCurrency(29.90)}/mês</td>
                  <td className="p-4 text-neutral-400">{formatDate(sub.created_at)}</td>
                  <td className="p-4 text-neutral-400">
                    {sub.cancel_at_period_end
                      ? <span className="text-red-400">Cancela em {formatDate(sub.current_period_end)}</span>
                      : formatDate(sub.current_period_end)
                    }
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-4 text-sm text-neutral-400">
          <span>
            Mostrando {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} de {total}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              aria-label="Página anterior"
              className="p-1 rounded hover:bg-white/10 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2">{page} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              aria-label="Próxima página"
              className="p-1 rounded hover:bg-white/10 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
